import {
    DirectDepositWithEarningsTile as DirectDepositWithEarningsTileComponent,
    MoneyMapEarningsTile as MoneyMapEarningsTileComponent,
} from '@fruitful/earnings-calculator';
import type { EventData } from '@fruitful/earnings-calculator/src/types/eventTracking';
import {
    type AnalyticsUserEvent,
    MoneyMapStatus,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { Animations, YStack } from 'smoothie';

import useGetAccountDisplayDetailsFromGlobalState from '../../banking/hooks/useGetAccountDisplayDetailsFromGlobalState';
import { selectedAccountForDirectDepositAtom } from '../../banking/state/SpacesAtoms';
import useGetDirectDepositAdoption from '../../bankingSwitch/hooks/useGetDirectDepositAdoption';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { holisticSummaryAtom } from '../../holistic/state/HolisticAtoms';
import { track } from '../../logger/Logger';

export const MoneyMapEarningsTileForMoneyMap = ({
    moneyMap,
    isMoneyMapCompleted,
    appContext = '',
}: {
    moneyMap: MoneyMapWithRulesAndAccounts;
    isMoneyMapCompleted: boolean;
    appContext?: string;
}) => {
    const { trackViewOnce } = useViewEvents();
    const { isDirectDepositAdoptionComplete, isLoadingDirectDepositAdoption } =
        useGetDirectDepositAdoption();
    const holisticSummary = useAtomValue(holisticSummaryAtom);

    const handleInteractionEvent = useCallback(
        (eventName: string, data?: EventData) => {
            track(eventName as AnalyticsUserEvent, data);
        },
        [],
    );

    if (
        !holisticSummary ||
        isLoadingDirectDepositAdoption ||
        (!isDirectDepositAdoptionComplete && isMoneyMapCompleted) // return null here because in this case a different calculator tile is shown elsewhere in the area
    ) {
        return null;
    }

    trackViewOnce(`${appContext}/MoneyMapEarningsTileForMoneyMap`);

    let calculatorPrimaryButtonText: string | undefined;
    let onPressCalculatorPrimaryButton: (() => void) | undefined;

    if (moneyMap.status === MoneyMapStatus.PREVIEW) {
        calculatorPrimaryButtonText = 'Explore your Money Map';
        onPressCalculatorPrimaryButton = () => {
            router.navigate('/roadmap/money-map');
        };
    } else if (
        moneyMap.status === MoneyMapStatus.ACTIVE &&
        !isMoneyMapCompleted
    ) {
        calculatorPrimaryButtonText = 'Set up your Money Map';
        onPressCalculatorPrimaryButton = () => {
            router.navigate('/roadmap/go/start');
        };
    }

    return (
        <MoneyMapEarningsTileComponent
            moneyMap={moneyMap}
            isMoneyMapCompleted={isMoneyMapCompleted}
            holisticSummary={holisticSummary}
            calculatorPrimaryButtonText={calculatorPrimaryButtonText}
            appContext={appContext}
            onPressCalculatorPrimaryButton={onPressCalculatorPrimaryButton}
            onInteractionEvent={handleInteractionEvent}
        />
    );
};

export const MoneyMapEarningsTileForDirectDeposit = ({
    variant,
    moneyMap,
    isMoneyMapCompleted,
    appContext = '',
}: {
    variant: 'simple' | 'dual-action';
    moneyMap?: MoneyMapWithRulesAndAccounts;
    isMoneyMapCompleted: boolean;
    appContext?: string;
}) => {
    const { trackViewOnce } = useViewEvents();
    const { isDirectDepositAdoptionComplete, isLoadingDirectDepositAdoption } =
        useGetDirectDepositAdoption();
    const holisticSummary = useAtomValue(holisticSummaryAtom);
    const incomeAccountId = moneyMap?.incomeMoneyMapAccount?.bankAccountId;
    const { account: incomeAccount } =
        useGetAccountDisplayDetailsFromGlobalState(
            {
                bankAccountId: incomeAccountId ?? '',
            },
            incomeAccountId != null,
        );
    const setAccountForDirectDeposit = useSetAtom(
        selectedAccountForDirectDepositAtom,
    );

    const handleInteractionEvent = useCallback(
        (eventName: string, data?: EventData) => {
            track(eventName as AnalyticsUserEvent, data);
        },
        [],
    );

    if (
        isDirectDepositAdoptionComplete || // if this is true, return nothing because the purpose of this component is to drive adoption of DD
        isLoadingDirectDepositAdoption ||
        !moneyMap ||
        !incomeAccount ||
        !holisticSummary ||
        !isMoneyMapCompleted ||
        moneyMap.status !== MoneyMapStatus.ACTIVE
    ) {
        return null;
    }

    trackViewOnce(
        `${appContext}/MoneyMapEarningsTileForDirectDeposit_${variant}`,
    );

    return variant === 'simple' ? (
        <YStack width="100%" maxWidth={350} alignSelf="center">
            <MoneyMapEarningsTileComponent
                moneyMap={moneyMap}
                isMoneyMapCompleted={isMoneyMapCompleted}
                holisticSummary={holisticSummary}
                calculatorPrimaryButtonText="Set up direct deposit"
                appContext={appContext}
                onInteractionEvent={handleInteractionEvent}
                onPressCalculatorPrimaryButton={() => {
                    setAccountForDirectDeposit(incomeAccount);
                    router.navigate({
                        pathname: '/banking/direct-deposit/info',
                        params: {
                            context: 'roadmap',
                            source: appContext,
                        },
                    });
                }}
            />
        </YStack>
    ) : variant === 'dual-action' ? (
        <YStack width="100%" maxWidth={500} {...Animations.FadeInFromAbove}>
            <DirectDepositWithEarningsTileComponent
                moneyMap={moneyMap}
                holisticSummary={holisticSummary}
                appContext="roadmap-tab"
                onInteractionEvent={handleInteractionEvent}
                onPressDirectDeposit={(source: string) => {
                    setAccountForDirectDeposit(incomeAccount);
                    router.navigate({
                        pathname: '/banking/direct-deposit/info',
                        params: {
                            context: 'roadmap',
                            source,
                        },
                    });
                }}
            />
        </YStack>
    ) : null;
};
